import React, { useState } from "react";
import { useAuth } from "../App";
import { toast } from "sonner";

const PRICING_PLANS = [
  {
    id: "monthly",
    name: "MONTHLY",
    price: 49,
    period: "/month",
    features: [
      "Full Dashboard Access",
      "Trade Journal",
      "Sniper Checklist",
      "AI Trading Coach",
      "Education Playbook",
      "Email Support",
    ],
    popular: false,
  },
  {
    id: "quarterly",
    name: "QUARTERLY",
    price: 129,
    period: "/quarter",
    savings: "Save $18",
    features: [
      "Everything in Monthly",
      "Priority AI Coach Access",
      "Weekly Market Analysis",
      "Trade Review Sessions",
      "Community Discord Access",
    ],
    popular: true,
  },
  {
    id: "annual",
    name: "ANNUAL",
    price: 399,
    period: "/year",
    savings: "Save $189",
    features: [
      "Everything in Quarterly",
      "1-on-1 Coaching Call (Monthly)",
      "Custom Checklist Templates",
      "Advanced Analytics",
      "Lifetime Updates",
    ],
    popular: false,
  },
];

const ZELLE_INFO = {
  email: "investolife2019@gmail.com",
  name: "Quarter to Close LLC",
  instructions: [
    "Open your bank's app or Zelle app",
    "Select 'Send Money'",
    "Enter the email above as recipient",
    "Enter the exact amount for your plan",
    "In the memo, include your registered email",
    "Complete the payment",
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 space-y-8" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-mono text-blue-500 tracking-widest mb-2">QUARTER TO CLOSE PRESENTS</p>
        <h1 className="text-3xl font-mono font-bold text-white mb-4">
          UNLOCK YOUR INSTITUTIONAL EDGE
        </h1>
        <p className="text-slate-400">
          Join the elite traders who've stopped losing to the rigged game. 
          Choose your plan and start trading like an institution.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {PRICING_PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`card-institutional p-6 relative ${
              plan.popular ? "border-blue-500 ring-1 ring-blue-500/20" : ""
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white text-xs font-mono px-3 py-1">
                  MOST POPULAR
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="font-mono text-lg text-white mb-2">{plan.name}</h3>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-mono font-bold text-white">${plan.price}</span>
                <span className="text-slate-500 font-mono text-sm">{plan.period}</span>
              </div>
              {plan.savings && (
                <p className="text-emerald-500 text-xs font-mono mt-2">{plan.savings}</p>
              )}
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                  <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSelectPlan(plan)}
              data-testid={`select-plan-${plan.id}`}
              className={`w-full py-3 font-mono text-sm tracking-wider transition-all ${
                plan.popular
                  ? "bg-blue-500 text-white hover:bg-blue-600"
                  : "bg-transparent border border-[#262626] text-slate-400 hover:text-white hover:border-slate-500"
              }`}
            >
              SELECT PLAN
            </button>
          </div>
        ))}
      </div>

      {/* Payment Modal */}
      {showPayment && selectedPlan && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="card-institutional max-w-lg w-full p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-mono text-xl text-white">COMPLETE PAYMENT</h2>
              <button
                onClick={() => setShowPayment(false)}
                className="text-slate-500 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Order Summary */}
            <div className="bg-[#050505] border border-[#262626] p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-400">Plan</span>
                <span className="font-mono text-white">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-slate-400">Account</span>
                <span className="font-mono text-blue-500">{user?.email}</span>
              </div>
              <div className="border-t border-[#262626] pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-white font-bold">Total</span>
                  <span className="font-mono text-2xl text-white">${selectedPlan.price}</span>
                </div>
              </div>
            </div>

            {/* Zelle Payment */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">Z</span>
                </div>
                <div>
                  <h3 className="font-mono text-white">Pay with Zelle</h3>
                  <p className="text-xs text-slate-500">Fast & secure bank transfer</p>
                </div>
              </div>

              <div className="bg-[#050505] border border-[#262626] p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-mono text-slate-500">SEND TO</span>
                  <button
                    onClick={() => copyToClipboard(ZELLE_INFO.email)}
                    className="text-xs font-mono text-blue-500 hover:text-blue-400"
                  >
                    {copied ? "COPIED!" : "COPY"}
                  </button>
                </div>
                <p className="font-mono text-white text-lg mb-1">{ZELLE_INFO.email}</p>
                <p className="text-sm text-slate-500">{ZELLE_INFO.name}</p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-mono text-slate-500">INSTRUCTIONS</p>
                {ZELLE_INFO.instructions.map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-[#171717] border border-[#262626] flex items-center justify-center text-xs font-mono text-blue-500 flex-shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-sm text-slate-400">{step}</p>
                  </div>
                ))}
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 mt-4">
                <p className="text-xs text-yellow-500">
                  <strong>IMPORTANT:</strong> Include your email ({user?.email}) in the payment memo. 
                  Your account will be activated within 24 hours of payment confirmation.
                </p>
              </div>

              <button
                onClick={() => {
                  toast.success("Payment instructions noted! Complete payment via Zelle.");
                  setShowPayment(false);
                }}
                className="w-full btn-primary py-3 mt-4"
              >
                I'VE COMPLETED PAYMENT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ */}
      <div className="max-w-2xl mx-auto mt-12">
        <h3 className="font-mono text-lg text-white mb-6 text-center">FREQUENTLY ASKED</h3>
        <div className="space-y-4">
          {[
            {
              q: "How long until my account is activated?",
              a: "Within 24 hours of receiving your Zelle payment, we'll activate your full access.",
            },
            {
              q: "Can I upgrade my plan later?",
              a: "Yes! Contact us and we'll prorate your upgrade based on remaining time.",
            },
            {
              q: "What if I'm not satisfied?",
              a: "We offer a 7-day money-back guarantee. No questions asked.",
            },
          ].map((faq, i) => (
            <div key={i} className="card-institutional p-4">
              <p className="font-mono text-sm text-white mb-2">{faq.q}</p>
              <p className="text-sm text-slate-400">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="text-center mt-8">
        <p className="text-slate-500 text-sm">
          Questions? Contact us at{" "}
          <a href="mailto:investolife2019@gmail.com" className="text-blue-500 hover:underline">
            investolife2019@gmail.com
          </a>
        </p>
        <p className="text-slate-600 text-xs mt-2 font-mono">
          © 2026 Quarter to Close LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
}
